import ApexTable, { FilterType, InputType, IProps } from 'apex-ui-table'
import { capitalize } from 'lodash'
import React from 'react'
import { FaArrowCircleRight } from 'react-icons/fa'
import { useResizeDetector } from 'react-resize-detector'
import { Link } from 'react-router-dom'
import { Account } from '../../types'
import { AccountTypeEnum } from '../../types/AccountTypeEnum'
import { Address, PhoneNumberDisplayMapping } from '../../types/CommonTypes'
import { RoutesEnum } from '../../types/RoutesEnum'
import { formatPhoneNumber } from '../../utils'
import SortIcons from '../SortIcons/SortIcons'
import styles from './AccountsTable.module.less'

export interface AccountsTableProps {
  accountResults: Account[]
}

interface AccountDisplayRow {
  isActive: boolean
  accountNumber: string
  accountTitle: string
  nameAndAddress: string[]
  phoneNumbers: string[]
  repCode: string
  accountType: string
  enterTradeId: string
  numberOfLines: number
}

const EMPTY_ADDRESS: Address = {
  streetAddress: [''],
  city: '',
  postalCode: '',
  state: '',
  country: '',
}
const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD HH:mm A'
const NON_BREAKING_SPACE = '\xa0'
const ACCOUNT_TYPE_OPTIONS_EXCEPT_SHORT = [
  { value: AccountTypeEnum.Cash, label: 'Cash' },
  { value: AccountTypeEnum.Dvp, label: 'DVP' },
  { value: AccountTypeEnum.Margin, label: 'Margin' },
]
const STATUS_OPTIONS = [
  {
    value: true,
    label: 'Open',
  },
  {
    value: false,
    label: 'Closed',
  },
]

const AccountsTable = ({ accountResults }: AccountsTableProps) => {
  const { width, height, ref } = useResizeDetector()

  const rows: AccountDisplayRow[] = accountResults.map(accountResult => {
    const {
      isActive = false,
      accountNumber,
      accountTitle = '',
      accountNames = [],
      accountAddress = EMPTY_ADDRESS,
      phoneNumbers = [],
      repCode = '',
      accountType = '',
    } = accountResult
    const formattedAddress = [
      ...accountAddress.streetAddress,
      `${accountAddress.city}, ${accountAddress.state || ''} ${accountAddress.postalCode || ''} ${
        accountAddress.country || ''
      }`,
    ]
    const name = accountNames.join(' ')
    const nameAndAddress = name ? [name].concat(formattedAddress) : formattedAddress
    const account: AccountDisplayRow = {
      isActive,
      accountNumber,
      accountTitle,
      nameAndAddress,
      phoneNumbers: phoneNumbers.map(
        ({ phoneNumberType, phoneNumber, extension }) =>
          `${PhoneNumberDisplayMapping[phoneNumberType]}: ${formatPhoneNumber(phoneNumber)} ${
            extension ? `x${extension}` : ''
          }`
      ),
      repCode,
      accountType: capitalize(accountType),
      enterTradeId: accountNumber,
      numberOfLines: Math.max(2, nameAndAddress.length, phoneNumbers.length),
    }
    return account
  })

  const tableProps: IProps = {
    maxColumnWidth: 9000,
    minColumnWidth: 10,
    defaultColumnWidth: 100,
    defaultDateFormat: DEFAULT_DATE_FORMAT,
    defaultHeaderHeight: 80,
    defaultRowHeight: 25,
    enableColumnSort: true,
    defaultSortAscending: true,
    defaultSortColumn: 'accountNumber',
    columns: {
      isActive: {
        label: 'Status',
        isVisible: true,
        width: 90,
        formatter: (isActive: boolean) => (
          <div className={`${isActive ? styles.openAccount : styles.closedAccount}`}>
            <span>{isActive ? 'Open' : 'Closed'}</span>
          </div>
        ),
        inputType: InputType.Select,
        options: STATUS_OPTIONS,
        filter: FilterType.Exact,
      },
      accountNumber: {
        label: 'Account',
        isVisible: true,
        width: 100,
        filter: FilterType.Text,
      },
      accountTitle: {
        label: 'Title',
        isVisible: true,
        width: 150,
        resizable: true,
        filter: FilterType.Text,
      },
      nameAndAddress: {
        label: 'Name and Address',
        isVisible: true,
        width: 200,
        formatter: (nameAndAddress: string[]) => (
          <div className={styles.nameAndAddress}>
            {nameAndAddress &&
              nameAndAddress.map &&
              nameAndAddress.map((line, lineIndex) => <span key={lineIndex}>{line}</span>)}
          </div>
        ),
        resizable: true,
        filter: FilterType.Text,
      },
      phoneNumbers: {
        label: 'Phone Numbers',
        isVisible: true,
        width: 200,
        formatter: (phoneNumbers: string[]) => (
          <div className={styles.phoneNumbers}>
            {phoneNumbers &&
              phoneNumbers.map &&
              phoneNumbers.map((phoneNumber, phoneIndex) => <span key={phoneIndex}>{phoneNumber}</span>)}
          </div>
        ),
        filter: FilterType.Text,
      },
      repCode: {
        label: 'Rep. Code',
        isVisible: true,
        width: 100,
        filter: FilterType.Text,
      },
      accountType: {
        label: 'Type',
        isVisible: true,
        width: 75,
        inputType: InputType.Select,
        options: ACCOUNT_TYPE_OPTIONS_EXCEPT_SHORT,
        filter: FilterType.Exact,
      },
      enterTradeId: {
        label: NON_BREAKING_SPACE,
        isVisible: true,
        width: 120,
        formatter: (enterTradeId: string) => (
          <div className={styles.enterTrade}>
            <Link to={`/${RoutesEnum.TradeEntry}/${enterTradeId}`}>
              <FaArrowCircleRight />
              &nbsp;Enter Trade
            </Link>
          </div>
        ),
        filter: FilterType.Text,
      },
    },
    rows,
    width,
    height,
    enableColumnFilters: true,
    sortIcons: {
      ascending: <SortIcons ascending={true} />,
      descending: <SortIcons ascending={false} />,
    },
  }

  return (
    <div className={`${styles.accountsTable} accounts-table`}>
      <div className={`${styles.tableHeader} table-header`}>
        <span>Results</span>
      </div>
      {
        // @ts-ignore
        <div className={`${styles.resizeAwareTableWrapper} resize-aware-table-wrapper`} ref={ref}>
          <ApexTable {...tableProps} />
        </div>
      }
    </div>
  )
}

export default AccountsTable
